import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAsync runs a request and exposes its state.
 *
 * Results are discarded if the component unmounts or the inputs change before
 * the response arrives. Without that guard a slow first request can overwrite
 * the result of a faster second one, so a filtered table briefly shows the
 * unfiltered data and looks like the filter silently failed.
 */
export function useAsync(fn, deps = [], { immediate = true } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const generation = useRef(0);

  const run = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fn();
      if (current === generation.current) setData(result);
      return result;
    } catch (err) {
      if (current === generation.current) setError(err);
      return undefined;
    } finally {
      if (current === generation.current) setLoading(false);
    }
    // fn is intentionally excluded: callers pass an inline closure, so
    // including it would re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (immediate) run();
    return () => {
      generation.current++;
    };
  }, [run, immediate]);

  return { data, error, loading, reload: run, setData };
}

/** useDebounced delays a rapidly changing value, for search inputs. */
export function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** useTheme persists the light or dark preference. */
export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("novahes.theme") || "light");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("novahes.theme", theme);
  }, [theme]);

  return [theme, () => setTheme((t) => (t === "light" ? "dark" : "light"))];
}
